/**
 * useNewItemsNotification Hook
 * 
 * React hook for detecting when new items are created by other users.
 * Useful for list/table views to show "X new items" notifications.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { itemUpdateStore, ItemUpdate } from '@/lib/stores/itemUpdateStore';
import { getCurrentUser } from '@/lib/stores/unifiedUserStore';

export type ItemType = 'object' | 'dialog' | 'document' | 'template' | 'flow';

export interface UseNewItemsNotificationOptions {
  /** Item type to watch for new creations */
  itemType: ItemType;
  /** Game ID */
  gameId: string | undefined;
  /** Whether to actively listen for new items */
  enabled?: boolean;
  /** Called when a new item is created by another user */
  onNewItem?: (itemId: string, itemType: string, createdBy: string) => void;
}

export interface UseNewItemsNotificationReturn {
  /** Number of new items created by others */
  newItemCount: number;
  /** List of new item IDs */
  newItemIds: string[];
  /** Clear the new items list */
  clearNewItems: () => void;
}

export function useNewItemsNotification({
  itemType,
  gameId,
  enabled = true,
  onNewItem,
}: UseNewItemsNotificationOptions): UseNewItemsNotificationReturn {
  const [newItems, setNewItems] = useState<Set<string>>(new Set());

  // Refs to track current state for callbacks
  const itemTypeRef = useRef(itemType);
  const gameIdRef = useRef(gameId);
  const enabledRef = useRef(enabled);
  const onNewItemRef = useRef(onNewItem);

  // Keep refs up to date
  useEffect(() => {
    itemTypeRef.current = itemType;
    gameIdRef.current = gameId;
    enabledRef.current = enabled;
    onNewItemRef.current = onNewItem;
  }, [itemType, gameId, enabled, onNewItem]);

  /**
   * Initialize store
   */
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!gameId || !currentUser || !enabled) return;

    // Initialize the store
    itemUpdateStore.initialize(gameId, currentUser.id);
  }, [gameId, enabled]);

  /**
   * Subscribe to created events for this item type
   */
  useEffect(() => {
    if (!enabled) return;

    console.log(`[useNewItemsNotification] Subscribing to created events for ${itemType}`);

    const unsubscribe = itemUpdateStore.subscribeToCreated(itemType, (update: ItemUpdate) => {
      console.log(`[useNewItemsNotification] New ${itemType} created:`, update);
      
      // Only track if it's a creation event
      if (update.action !== 'created') return;

      // Add to our set of new items
      setNewItems((prev) => {
        const newSet = new Set(prev);
        newSet.add(update.item_id);
        return newSet;
      });

      // Call the callback
      const createdByName = update.profiles?.full_name || 
                           update.profiles?.username || 
                           update.profiles?.email || 
                           'Unknown User';
      
      onNewItemRef.current?.(update.item_id, update.item_type, createdByName);
    });

    return () => {
      console.log(`[useNewItemsNotification] Unsubscribing from created events for ${itemType}`);
      unsubscribe();
    };
  }, [itemType, enabled]);

  /**
   * Clear new items list
   */
  const clearNewItems = useCallback(() => {
    console.log(`[useNewItemsNotification] Clearing ${newItems.size} new items`);
    setNewItems(new Set());
  }, [newItems.size]);

  return {
    newItemCount: newItems.size,
    newItemIds: Array.from(newItems),
    clearNewItems,
  };
}

export default useNewItemsNotification;
